import { useEffect, useRef, useState } from "react";
import type { Dataset } from "../lib/types";
import {
  PROVIDER_LIST,
  getProvider,
  isConfigured,
  runChat,
  LlmError,
  type ChatMessage,
  type LlmProviderId,
  type LlmSettings,
} from "../lib/llm";

interface Props {
  data: Dataset;
}

// The LLM connection (provider + key + model) is held in sessionStorage, so it
// stays live for this one browser/login session: it survives tab switches and
// page reloads in the same tab, but is wiped when the tab closes, on sign-out,
// and is never synced to other devices (unlike localStorage). Production
// deployments proxy through an Azure backend — the key never reaches the SPA.
const CHAT_SETTINGS_KEY = "cs.chat.llm";

function loadChatSettings(): LlmSettings | null {
  try {
    const raw = sessionStorage.getItem(CHAT_SETTINGS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LlmSettings;
    if (!parsed?.provider || !parsed?.apiKey || !parsed?.model) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveChatSettings(settings: LlmSettings | null) {
  try {
    if (settings) sessionStorage.setItem(CHAT_SETTINGS_KEY, JSON.stringify(settings));
    else sessionStorage.removeItem(CHAT_SETTINGS_KEY);
  } catch {}
}

const STARTERS = [
  "Summarise the headline cash position right now.",
  "Which customers belong on the watchlist this week?",
  "What's driving the K-001 over/underbilling number?",
  "Walk me through the active commodity flags.",
  "If clean-room mix drops to 7%, what happens to the forecast?",
];

export default function Chat({ data }: Props) {
  // Settings live in sessionStorage (see loadChatSettings) so they persist for
  // the current login session — across tab switches and reloads — but no longer.
  const [settings, setSettings] = useState<LlmSettings | null>(loadChatSettings);
  const [showSettings, setShowSettings] = useState(() => !isConfigured(loadChatSettings()));
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const configured = isConfigured(settings);

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || busy || !configured) return;
    setError(null);
    const userMsg: ChatMessage = { role: "user", content: trimmed, ts: Date.now() };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const res = await runChat(next, settings, data);
      setMessages((cur) => [
        ...cur,
        {
          role: "assistant",
          content: res.content || "(empty response)",
          ts: Date.now(),
          via: `${settings.provider}/${res.model}`,
        },
      ]);
    } catch (e) {
      const msg = e instanceof LlmError
        ? e.message
        : e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setMessages([]);
    setError(null);
  }

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">
            Chat <span className="page-pill">ASK ANYTHING</span>
          </h1>
          <p className="page-sub">
            Connect any LLM (OpenAI, Anthropic Claude, Google Gemini) with your own API key
            and ask questions about the live snapshot. The dashboard sends a structured
            context — KPI catalog, current values, top customers, distress events,
            commodity flags — alongside your message.
          </p>
        </div>
        <div className="page-controls">
          {configured && (
            <span className="chat-pill">
              <span className="chat-pill-dot" />
              {getProvider(settings.provider).label} · {settings.model}
            </span>
          )}
          <button className="btn-ghost" onClick={() => setShowSettings((v) => !v)}>
            {showSettings ? "Hide settings" : "Settings"}
          </button>
          <button className="btn-ghost" onClick={reset} disabled={messages.length === 0}>
            New chat
          </button>
        </div>
      </div>

      {showSettings && (
        <SettingsPanel
          initial={settings}
          onSave={(s) => {
            setSettings(s);
            saveChatSettings(s);
            setShowSettings(false);
            setError(null);
          }}
          onClear={() => {
            setSettings(null);
            saveChatSettings(null);
            setMessages([]);
            setShowSettings(true);
          }}
        />
      )}

      <div className="chat-shell">
        <div className="chat-thread" ref={scrollRef}>
          {messages.length === 0 && !busy && (
            <div className="chat-empty">
              <h2 className="chat-empty-title">Ask the dashboard.</h2>
              <p className="chat-empty-sub">
                Pick a starter or type your own. Every claim the model returns will cite
                the K-IDs it derives from.
              </p>
              <div className="chat-starters">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="chat-starter"
                    onClick={() => send(s)}
                    disabled={!configured}
                    title={!configured ? "Configure provider in Settings first" : undefined}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {!configured && (
                <div className="chat-empty-hint">
                  No provider configured yet — open <strong>Settings</strong> to plug in
                  an OpenAI, Anthropic, or Gemini API key.
                </div>
              )}
            </div>
          )}

          {messages.map((m, i) => (
            <article key={i} className={`chat-msg ${m.role}`}>
              <div className="chat-msg-head">
                <span className="chat-msg-role">{m.role === "user" ? "You" : "Assistant"}</span>
                {m.via && <span className="chat-msg-via">{m.via}</span>}
              </div>
              <div className="chat-msg-body">{m.content}</div>
            </article>
          ))}

          {busy && (
            <article className="chat-msg assistant pending">
              <div className="chat-msg-head">
                <span className="chat-msg-role">Assistant</span>
                <span className="chat-msg-via">thinking…</span>
              </div>
              <div className="chat-msg-body">
                <span className="chat-typing"><span /><span /><span /></span>
              </div>
            </article>
          )}

          {error && (
            <div className="chat-error">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        <form
          className="chat-input"
          onSubmit={(e) => {
            e.preventDefault();
            send(input);
          }}
        >
          <textarea
            className="chat-textarea"
            placeholder={configured ? "Ask about the dashboard…" : "Configure a provider in Settings first"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            rows={2}
            disabled={!configured || busy}
          />
          <button
            type="submit"
            className="chat-send"
            disabled={!configured || busy || !input.trim()}
            aria-label="Send"
          >
            ↑
          </button>
        </form>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Settings panel
// ---------------------------------------------------------------------------

interface SettingsPanelProps {
  initial: LlmSettings | null;
  onSave: (s: LlmSettings) => void;
  onClear: () => void;
}

function SettingsPanel({ initial, onSave, onClear }: SettingsPanelProps) {
  const initProvider: LlmProviderId = initial?.provider ?? "openai";
  const [provider, setProvider] = useState<LlmProviderId>(initProvider);
  const [apiKey, setApiKey] = useState(initial?.apiKey ?? "");
  const [model, setModel] = useState(initial?.model ?? getProvider(initProvider).defaultModel);
  const [showKey, setShowKey] = useState(false);
  // Model selection defaults to Auto (use the provider's recommended default).
  // A saved session counts as Manual only if its model differs from that
  // default — i.e. the user deliberately typed something else.
  const [manual, setManual] = useState(
    !!initial && initial.model.trim() !== getProvider(initProvider).defaultModel,
  );

  function pickProvider(id: LlmProviderId) {
    setProvider(id);
    // Reset the model field to the new provider's default so the user
    // doesn't carry a stale model name across providers.
    setModel(getProvider(id).defaultModel);
  }

  const provDef = getProvider(provider);
  // What actually gets sent: the typed value in Manual mode, the provider's
  // recommended default in Auto mode.
  const effectiveModel = manual ? model.trim() : provDef.defaultModel;

  return (
    <div className="chat-settings">
      <div className="chat-settings-head">
        <div>
          <div className="chat-settings-eyebrow">LLM connection</div>
          <h2 className="chat-settings-title">Plug-and-play provider settings</h2>
          <p className="chat-settings-sub">
            Bring your own API key. Held for this login session only — it stays live across
            tab changes and reloads, and is wiped on sign-out or when you close the tab. It
            never syncs to other devices. Production deployments proxy through an Azure
            backend — keys never leave the tenant.
          </p>
        </div>
      </div>

      <div className="chat-settings-grid">
        <div className="chat-settings-field">
          <label className="chat-settings-label">Provider</label>
          <div className="chat-settings-providers">
            {PROVIDER_LIST.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`chat-settings-prov${provider === p.id ? " active" : ""}`}
                onClick={() => pickProvider(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="chat-settings-field">
          <label className="chat-settings-label" htmlFor="chat-key">API key</label>
          <div className="chat-settings-key-row">
            <input
              id="chat-key"
              type={showKey ? "text" : "password"}
              className="chat-settings-input"
              autoComplete="off"
              value={apiKey}
              placeholder={
                provider === "openai" ? "sk-…" :
                provider === "anthropic" ? "sk-ant-…" :
                "AIza…"
              }
              onChange={(e) => setApiKey(e.target.value)}
            />
            <button
              type="button"
              className="chat-settings-eye"
              onClick={() => setShowKey((v) => !v)}
              aria-label={showKey ? "Hide key" : "Show key"}
            >
              {showKey ? "hide" : "show"}
            </button>
          </div>
        </div>

        <div className="chat-settings-field">
          <div className="chat-settings-model-head">
            <label className="chat-settings-label" htmlFor="chat-model">Model</label>
            <div className="chat-settings-providers" role="tablist" aria-label="Model selection mode">
              <button
                type="button"
                className={`chat-settings-prov${!manual ? " active" : ""}`}
                aria-pressed={!manual}
                onClick={() => setManual(false)}
              >
                Auto
              </button>
              <button
                type="button"
                className={`chat-settings-prov${manual ? " active" : ""}`}
                aria-pressed={manual}
                onClick={() => {
                  setManual(true);
                  if (!model.trim()) setModel(provDef.defaultModel);
                }}
              >
                Manual
              </button>
            </div>
          </div>
          {manual ? (
            <>
              <input
                id="chat-model"
                list={`chat-models-${provider}`}
                className="chat-settings-input"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder={provDef.defaultModel}
              />
              <datalist id={`chat-models-${provider}`}>
                {provDef.modelHints.map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
              <div className="chat-settings-hint">
                Suggested: {provDef.modelHints.slice(0, 4).join(" · ")}
              </div>
            </>
          ) : (
            <div className="chat-settings-auto">
              <span className="chat-settings-auto-model">{provDef.defaultModel}</span>
              <span className="chat-settings-hint">
                Picked automatically for {provDef.label} — switch to Manual to type any model.
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="chat-settings-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => onSave({ provider, apiKey: apiKey.trim(), model: effectiveModel })}
          disabled={!apiKey.trim() || !effectiveModel}
        >
          Save & connect
        </button>
        {initial && (
          <button type="button" className="btn-ghost" onClick={onClear}>
            Wipe key & messages
          </button>
        )}
      </div>
    </div>
  );
}
